"use client";

import * as React from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import {
  circleActionVariantCssVars,
  resolveCircleActionVariant,
  type CircleActionVariant,
} from "./circle-action-variants";

export interface GlassBadgeGroupItem {
  value: string;
  label?: React.ReactNode;
  icon?: React.ReactNode;
  variant?: CircleActionVariant;
  disabled?: boolean;
}

export interface GlassBadgeGroupProps {
  items: GlassBadgeGroupItem[];
  /** Pass null/undefined to show no active bubble. */
  activeValue?: string | null;
  onChange?: (value: string) => void;
  onHover?: (value: string | null) => void;
  size?: "sm" | "md";
  className?: never;
}

export function GlassBadgeGroup({
  items,
  activeValue,
  onChange,
  onHover,
  size = "sm",
  className,
}: GlassBadgeGroupProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const bubbleRef    = useRef<HTMLDivElement>(null);
  const itemRefs     = useRef<(HTMLButtonElement | null)[]>([]);

  const [bubble, setBubble]           = useState<{ left: number; width: number } | null>(null);
  const [animate, setAnimate]         = useState(false);
  const [isMoving, setIsMoving]       = useState(false);
  const movingTimerRef                = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Internal hover tracking — drives the bubble position
  const [hoveredValue, setHoveredValue] = useState<string | null>(null);

  // The bubble follows hover when present, otherwise rests on active
  const displayTarget = hoveredValue ?? activeValue ?? null;
  // The bubble color follows the current visual target: hover first, then active.
  const displayItem = items.find((i) => i.value === displayTarget);
  const displayVariant = resolveCircleActionVariant(displayItem?.variant ?? "slate");
  const displayVars = circleActionVariantCssVars(displayVariant);

  const triggerSquish = useCallback(() => {
    const el = bubbleRef.current;
    if (!el) return;
    el.style.animation = "none";
    void el.offsetHeight;
    el.style.animation = "gb-liquid 560ms cubic-bezier(0.34,1.2,0.64,1) both";
  }, []);

  // Measure position of any target item
  const measureAt = useCallback((target: string) => {
    const idx = items.findIndex((i) => i.value === target);
    const el  = itemRefs.current[idx];
    const container = containerRef.current;
    if (!el || !container) return null;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    return { left: eRect.left - cRect.left, width: eRect.width };
  }, [items]);

  // Refs to track previous values for squish logic
  const prevDisplayTarget = useRef<string | null>(null);
  const prevActiveValue   = useRef<string | null | undefined>(undefined);

  useLayoutEffect(() => {
    if (!displayTarget) {
      setBubble(null);
      setAnimate(false);
      prevDisplayTarget.current = null;
      prevActiveValue.current   = activeValue ?? null;
      return;
    }

    const pos = measureAt(displayTarget);
    if (!pos) return;

    const wasNull     = prevDisplayTarget.current == null;
    const moved       = prevDisplayTarget.current !== displayTarget;

    prevDisplayTarget.current = displayTarget;
    prevActiveValue.current   = activeValue ?? null;

    setBubble(pos);

    if (wasNull) {
      // First appearance — snap to position, then enable spring
      setAnimate(false);
      requestAnimationFrame(() => requestAnimationFrame(() => setAnimate(true)));
    } else if (moved && animate) {
      // Liquid deformation + subtle motion blur on any bubble travel.
      triggerSquish();
      if (movingTimerRef.current) clearTimeout(movingTimerRef.current);
      setIsMoving(true);
      movingTimerRef.current = setTimeout(() => setIsMoving(false), 520);
    }
  }, [displayTarget, activeValue, hoveredValue, measureAt, animate, triggerSquish]);

  useEffect(() => {
    return () => {
      if (movingTimerRef.current) clearTimeout(movingTimerRef.current);
    };
  }, []);

  // Re-measure on container resize
  useEffect(() => {
    if (!displayTarget) return;
    const ro = new ResizeObserver(() => {
      const pos = measureAt(displayTarget);
      if (pos) setBubble(pos);
    });
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [displayTarget, measureAt]);

  const padClass  = size === "sm" ? "px-3 py-[5px]"   : "px-3.5 py-[7px]";
  const textClass = size === "sm" ? "text-[11px]"      : "text-xs";

  return (
    <div
      ref={containerRef}
      className={cn("relative flex items-center gap-1.5", className)}
    >
      {/* ── Single sliding bubble ─────────────────────────────────────────── */}
      <div
        ref={bubbleRef}
        aria-hidden
        className="gb-group-bubble pointer-events-none absolute inset-y-0 rounded-full"
        data-moving={isMoving ? "true" : undefined}
        style={{
          left:    bubble?.left  ?? 0,
          width:   bubble?.width ?? 0,
          opacity: bubble ? 1 : 0,
          transition: animate
            ? [
                "left 500ms cubic-bezier(0.22,1,0.36,1)",
                "width 540ms cubic-bezier(0.22,1.1,0.36,1)",
                "opacity 240ms ease",
              ].join(",")
            : "opacity 240ms ease",
          ...(displayVars as React.CSSProperties),
        }}
      />

      {/* ── Items — transparent, bubble does all visual heavy lifting ──────── */}
      {items.map((item, idx) => {
        const isActive = item.value === activeValue;
        const variant  = resolveCircleActionVariant(item.variant ?? "slate");
        const vars     = circleActionVariantCssVars(variant);

        return (
          <button
            key={item.value}
            ref={(el) => { itemRefs.current[idx] = el; }}
            type="button"
            disabled={item.disabled}
            onClick={() => onChange?.(item.value)}
            onMouseEnter={() => {
              setHoveredValue(item.value);
              onHover?.(item.value);
            }}
            onMouseLeave={() => {
              setHoveredValue(null);
              onHover?.(null);
            }}
            style={vars as React.CSSProperties}
            className={cn(
              "gb-group-item relative z-[1] inline-flex shrink-0 items-center gap-1.5 rounded-full",
              "select-none font-medium tracking-[-0.01em]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 focus-visible:ring-offset-1",
              "disabled:pointer-events-none disabled:opacity-40",
              "motion-safe:[transition:color_280ms_ease,transform_300ms_cubic-bezier(0.34,1.56,0.64,1)]",
              padClass,
              textClass,
              isActive ? "gb-group-item--active" : "gb-group-item--idle",
            )}
          >
            {item.icon   && <span className="flex-shrink-0">{item.icon}</span>}
            {item.label != null && <span className="whitespace-nowrap">{item.label}</span>}
          </button>
        );
      })}

      <style jsx global>{`
        /* ── Liquid squish keyframe ───────────────────────────────────────── */
        @keyframes gb-liquid {
          0%   { transform: scaleX(1)    scaleY(1); }
          22%  { transform: scaleX(1.22) scaleY(0.94); }
          46%  { transform: scaleX(0.94) scaleY(1.03); }
          68%  { transform: scaleX(1.08) scaleY(0.98); }
          100% { transform: scaleX(1)    scaleY(1); }
        }

        /* ── Water-expand keyframe (used by NewMessage panel) ────────────── */
        @keyframes gb-water-expand {
          from { clip-path: ellipse(48% 0% at 50% 0%); opacity: 0; }
          30%  { opacity: 1; }
          to   { clip-path: ellipse(140% 180% at 50% 0%); opacity: 1; }
        }
        .gb-water-expand {
          animation: gb-water-expand 520ms cubic-bezier(0.22,1,0.36,1) both;
        }

        /* ── Bubble — tinted (on active item) ────────────────────────────── */
        .gb-group-bubble {
          overflow: hidden;
          isolation: isolate;
          background:
            radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255,255,255,0.50) 0%, transparent 58%),
            radial-gradient(circle at 46% 44%, rgba(255,255,255,0.74) 0%, rgba(255,255,255,0.28) 38%, rgba(255,255,255,0.06) 58%, transparent 72%),
            radial-gradient(circle at 70% 68%, rgba(255,255,255,0.38) 0%, rgba(255,255,255,0.12) 20%, transparent 34%),
            var(--gb-tint-bg);
          backdrop-filter: blur(22px) saturate(1.55);
          -webkit-backdrop-filter: blur(22px) saturate(1.55);
          box-shadow:
            inset -1px -1px 0 rgba(255,255,255,0.68),
            inset -1px  0   0 rgba(255,255,255,0.20),
            inset  0   -1px 0 rgba(255,255,255,0.32),
            inset  0    0   0 1.5px var(--gb-tint-border),
            inset  1px  1px 0 rgba(15,23,42,0.05),
            0 1px 3px rgba(15,23,42,0.06),
            var(--gb-tint-shadow);
        }
        @keyframes gb-inner-surface-warp {
          0% {
            opacity: 0;
            transform: translateX(-62%) scaleX(0.48) skewX(-10deg);
            filter: blur(8px) saturate(1.35);
          }
          24% {
            opacity: 0.72;
            transform: translateX(-26%) scaleX(1.85) skewX(-6deg);
            filter: blur(7px) saturate(1.55);
          }
          58% {
            opacity: 0.52;
            transform: translateX(18%) scaleX(1.34) skewX(4deg);
            filter: blur(5px) saturate(1.45);
          }
          100% {
            opacity: 0;
            transform: translateX(66%) scaleX(0.52) skewX(0deg);
            filter: blur(8px) saturate(1.25);
          }
        }

        @keyframes gb-inner-refraction {
          0% {
            opacity: 0;
            transform: translateX(-68%) scaleX(0.32) skewX(-16deg);
          }
          30% {
            opacity: 0.9;
            transform: translateX(-22%) scaleX(2.05) skewX(-7deg);
          }
          68% {
            opacity: 0.56;
            transform: translateX(24%) scaleX(1.22) skewX(5deg);
          }
          100% {
            opacity: 0;
            transform: translateX(70%) scaleX(0.38) skewX(0deg);
          }
        }

        .gb-group-bubble::after {
          content: "";
          position: absolute;
          inset: -34%;
          pointer-events: none;
          border-radius: inherit;
          opacity: 0;
          background: inherit;
          background-size: 190% 160%;
          filter: blur(7px) saturate(1.55) contrast(1.12);
          mix-blend-mode: soft-light;
          z-index: 1;
        }

        .gb-group-bubble::before {
          content: "";
          position: absolute;
          inset: -30%;
          pointer-events: none;
          border-radius: inherit;
          opacity: 0;
          background:
            linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 24%, rgba(255,255,255,0.62) 45%, rgba(255,255,255,0.20) 62%, transparent 100%),
            radial-gradient(ellipse 24% 74% at 45% 50%, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.20) 42%, transparent 72%);
          z-index: 2;
        }

        .gb-group-bubble[data-moving="true"]::after {
          animation: gb-inner-surface-warp 520ms cubic-bezier(0.16,1,0.3,1) both;
        }

        .gb-group-bubble[data-moving="true"]::before {
          animation: gb-inner-refraction 520ms cubic-bezier(0.16,1,0.3,1) both;
        }

        .dark .gb-group-bubble::after {
          filter: blur(7px) saturate(1.4) contrast(1.08) brightness(1.08);
          mix-blend-mode: screen;
        }

        .dark .gb-group-bubble::before {
          background:
            linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.04) 24%, rgba(255,255,255,0.32) 45%, rgba(255,255,255,0.10) 62%, transparent 100%),
            radial-gradient(ellipse 24% 74% at 45% 50%, rgba(255,255,255,0.38) 0%, rgba(255,255,255,0.12) 42%, transparent 72%);
        }

        .dark .gb-group-bubble {
          background:
            radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255,255,255,0.20) 0%, transparent 58%),
            radial-gradient(circle at 46% 44%, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.08) 40%, transparent 70%),
            radial-gradient(circle at 70% 68%, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.04) 20%, transparent 34%),
            var(--gb-tint-bg);
          backdrop-filter: blur(22px) saturate(1.45) brightness(0.98);
          -webkit-backdrop-filter: blur(22px) saturate(1.45) brightness(0.98);
          box-shadow:
            inset -1px -1px 0 rgba(255,255,255,0.22),
            inset -1px  0   0 rgba(255,255,255,0.10),
            inset  0   -1px 0 rgba(255,255,255,0.14),
            inset  0    0   0 1.5px var(--gb-tint-border),
            inset  1px  1px 0 rgba(0,0,0,0.20),
            0 1px 3px rgba(0,0,0,0.20),
            var(--gb-tint-shadow);
        }

        /* ── Bubble — grey-water (hovering an idle/non-active item) ─────── */
        .gb-group-bubble[data-is-idle] {
          background:
            radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255,255,255,0.54) 0%, transparent 58%),
            radial-gradient(circle at 46% 44%, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.36) 38%, rgba(255,255,255,0.08) 58%, transparent 72%),
            radial-gradient(circle at 70% 68%, rgba(255,255,255,0.46) 0%, rgba(255,255,255,0.16) 20%, transparent 34%),
            radial-gradient(circle at 50% 50%, rgba(203,213,225,0.18) 0%, rgba(226,232,240,0.08) 100%),
            rgba(226,232,240,0.52);
          backdrop-filter: blur(22px) saturate(1.35);
          -webkit-backdrop-filter: blur(22px) saturate(1.35);
          box-shadow:
            inset -1px -1px 0 rgba(255,255,255,0.72),
            inset -1px  0   0 rgba(255,255,255,0.22),
            inset  0   -1px 0 rgba(255,255,255,0.36),
            inset  0    0   0 1.5px rgba(203,213,225,0.50),
            inset  1px  1px 0 rgba(15,23,42,0.06),
            0 1px 3px rgba(15,23,42,0.06),
            0 4px 12px -4px rgba(15,23,42,0.08);
        }
        .dark .gb-group-bubble[data-is-idle] {
          background:
            radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255,255,255,0.22) 0%, transparent 58%),
            radial-gradient(circle at 46% 44%, rgba(255,255,255,0.32) 0%, rgba(255,255,255,0.10) 40%, transparent 70%),
            radial-gradient(circle at 70% 68%, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.05) 20%, transparent 34%),
            radial-gradient(circle at 50% 50%, rgba(148,163,184,0.12) 0%, rgba(100,116,139,0.05) 100%),
            rgba(148,163,184,0.09);
          backdrop-filter: blur(22px) saturate(1.25) brightness(0.96);
          -webkit-backdrop-filter: blur(22px) saturate(1.25) brightness(0.96);
          box-shadow:
            inset -1px -1px 0 rgba(255,255,255,0.24),
            inset -1px  0   0 rgba(255,255,255,0.10),
            inset  0   -1px 0 rgba(255,255,255,0.16),
            inset  0    0   0 1.5px rgba(100,116,139,0.30),
            inset  1px  1px 0 rgba(0,0,0,0.22),
            0 1px 3px rgba(0,0,0,0.20),
            0 4px 12px -4px rgba(0,0,0,0.28);
        }

        /* ── Item colours — flat, no background (bubble does the glass) ─── */
        .gb-group-item--idle        { color: rgb(100 116 139); }
        .dark .gb-group-item--idle  { color: rgb(161 161 170); }
        .gb-group-item--active      { color: var(--gb-accent-color); }
        .dark .gb-group-item--active { color: var(--gb-accent-color-dark); }

        .gb-group-item:not(:disabled):active { transform: scale(0.97); }

        /* Colour shift on hover — shows the item's own accent */
        .gb-group-item--idle:not(:disabled):hover       { color: var(--gb-accent-color); }
        .dark .gb-group-item--idle:not(:disabled):hover { color: var(--gb-accent-color-dark); }

        /* ── Icon colours ─────────────────────────────────────────────────── */
        .gb-group-item--idle img, .gb-group-item--idle svg {
          filter: var(--cab-icon-filter-idle, none);
          opacity: 0.78;
          transition: filter 280ms ease, opacity 280ms ease;
        }
        .dark .gb-group-item--idle img, .dark .gb-group-item--idle svg {
          filter: var(--cab-icon-filter-idle-dark, none);
          opacity: 0.62;
        }
        .gb-group-item--idle:not(:disabled):hover img,
        .gb-group-item--idle:not(:disabled):hover svg {
          filter: var(--cab-icon-filter-idle, none);
          opacity: 0.90;
        }
        .dark .gb-group-item--idle:not(:disabled):hover img,
        .dark .gb-group-item--idle:not(:disabled):hover svg {
          filter: var(--cab-icon-filter-idle-dark, none);
          opacity: 0.85;
        }
        .gb-group-item--active img, .gb-group-item--active svg {
          filter: var(--cab-icon-filter-idle, none);
          opacity: 0.90;
          transition: filter 280ms ease, opacity 280ms ease;
        }
        .dark .gb-group-item--active img, .dark .gb-group-item--active svg {
          filter: var(--cab-icon-filter-idle-dark, none);
          opacity: 0.85;
        }
      `}</style>
    </div>
  );
}
